import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const ChatItem = ({ chat, isActive, onSelect, onRename, onDelete }) => {
  const [menuOpen, setMenuOpen]     = useState(false)
  const [isEditing, setIsEditing]   = useState(false)
  const [draftTitle, setDraftTitle] = useState(chat.title)
  const itemRef  = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return undefined
    const onClickOutside = e => { if (!itemRef.current.contains(e.target)) setMenuOpen(false) }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [menuOpen])

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.select()
  }, [isEditing])

  const startRename = () => {
    setMenuOpen(false)
    setDraftTitle(chat.title)
    setIsEditing(true)
  }

  const commitRename = () => {
    setIsEditing(false)
    const title = draftTitle.trim()
    if (title && title !== chat.title) onRename(chat.id, title)
  }

  const onKeyDown = e => {
    if (e.key === 'Enter')  commitRename()
    if (e.key === 'Escape') setIsEditing(false)
  }

  return (
    <li ref={itemRef} className="mcp-sidebar__item-wrapper">
      <div
        className={`mcp-sidebar__item ${isActive ? 'is-active' : ''}`}
        onClick={() => !isEditing && onSelect(chat.id)}
      >
        {chat.pending && (
          <FontAwesomeIcon
            icon="circle-notch"
            spin
            className="mcp-sidebar__item-spinner"
            title={I18n.t('mcp_chat.generating')}
          />
        )}

        {isEditing ? (
          <input
            ref={inputRef}
            className="mcp-sidebar__item-input"
            value={draftTitle}
            onChange={e => setDraftTitle(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commitRename}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span className="mcp-sidebar__item-title">{chat.title}</span>
        )}

        {!isEditing && (
          <button
            type="button"
            className="mcp-sidebar__item-menu-btn"
            aria-label={I18n.t('mcp_chat.options')}
            onClick={e => { e.stopPropagation(); setMenuOpen(open => !open) }}
          >
            <FontAwesomeIcon icon="ellipsis-v" />
          </button>
        )}
      </div>

      {menuOpen && (
        <div className="mcp-sidebar__menu" onClick={e => e.stopPropagation()}>
          <button type="button" className="mcp-sidebar__menu-item" onClick={startRename}>
            <FontAwesomeIcon icon="pen" />
            {I18n.t('mcp_chat.rename')}
          </button>
          <button
            type="button"
            className="mcp-sidebar__menu-item is-danger"
            onClick={() => { setMenuOpen(false); onDelete(chat.id) }}
          >
            <FontAwesomeIcon icon="trash" />
            {I18n.t('mcp_chat.delete')}
          </button>
        </div>
      )}
    </li>
  )
}

const Sidebar = ({ chats, currentChatId, onNewChat, onSelect, onRename, onDelete }) => (
  <aside className="mcp-sidebar">
    <button type="button" className="mcp-sidebar__new" onClick={onNewChat}>
      <FontAwesomeIcon icon="plus" />
      {I18n.t('mcp_chat.new_chat')}
    </button>

    <ul className="mcp-sidebar__list">
      {chats.map(chat => (
        <ChatItem
          key={chat.id}
          chat={chat}
          isActive={chat.id === currentChatId}
          onSelect={onSelect}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
    </ul>
  </aside>
)

export default Sidebar
